import React, { useEffect, useState } from 'react';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { Col, Spinner } from 'react-bootstrap';
import CarouselComponent from '../Generic/CarouselComponent';
import ProductModal from './ProductModal';
import DeleteButton from '../Generic/DeleteButton';
import {
  deleteProduct,
  fetchProductLogs,
  getProductDetailsDirect,
  getProductDetailsOptimized,
} from '../../clients/product_client';
import SupplierDetailModal from '../Supplier/SupplierDetailModal';
import ManufacturerDetailModal from '../Manufacturer/ManufacturerDetailModal';
import UpdateAmountModal from './UpdateAmountModal';
import './ProductComponentStyle.css';
import {
  calculatePriceAfterDiscount,
  formatDecimalNumber,
  getCurrencySymbol,
  getExpiryCategoryLabel,
  isKitProduct,
  getEffectiveStock,
  getKitStockDisplay,
} from '../../config_and_helpers/helpers';
import { CURRENCY_SYMBOLS } from '../../config_and_helpers/config';
import SimilarProductsTable from './SimilarProductsTable';
import NavigateToUrlIcon from '../Generic/NavigateToUrlIcon';
import StockItemLogsTable from './StockItemLogsTable';
import StockItemTable from './StockItemTable';
import KitInventoryPanel from './KitInventoryPanel';
import ProductOpeningStatisticsSection from './ProductOpeningStatisticsSection';
import { logError, logInfo } from '../../config_and_helpers/logger';
import { useLabStorageNodes } from '../Storage/useLabStorageNodes';
import { resolveOpenedLotCount } from '../../utils/openedLotCount';
import {
  allStockItemsHaveStorageNode,
  groupLotLocations,
} from '../../utils/storagePlace';
import ProductLotLocations from './ProductLotLocations';
import OpenedLotCount from './OpenedLotCount';

/**
 * Represents a modal component for viewing component product details in multi-component products.
 * This is a simplified version of InventoryModal specifically for component products.
 *
 * @param {string} token - The authentication token.
 * @param {boolean} isSupplier - Flag indicating if the user is a supplier.
 * @param {Object} componentData - The component data from multi_component_status.
 * @param {function} updateProducts - Function to update the products list.
 * @param {function} clearSearchValue - Function to clear the search value.
 * @param {function} handleProductUpdateFromSupplierTable - Function to re-fetch the suppliers list.
 */
const ComponentInventoryModal = ({
  token,
  isSupplier,
  componentData,
  updateProducts,
  clearSearchValue,
  handleProductUpdateFromSupplierTable,
}) => {
  // State for controlling the visibility of the modal
  const [show, setShow] = useState(false);
  const isViewer = useIsLabViewer();
  const { nodes: storageNodes, ready: storageReady } = useLabStorageNodes(
    token,
    show
  );
  const storageTree = storageReady ? storageNodes : null;

  // State for the full product details
  const [product, setProduct] = useState(null);

  // State for loading
  const [isLoading, setIsLoading] = useState(false);

  // State for product logs
  const [productLogs, setProductLogs] = useState([]);

  // Function to close the modal
  const handleClose = () => {
    setShow(false);
    setProduct(null);
    setProductLogs([]);
  };

  // Function to show the modal and fetch product details
  const handleShow = async () => {
    setShow(true);
    setIsLoading(true);

    try {
      // Fetch the full product details using the component ID
      const productDetails = await getProductDetailsOptimized(
        token,
        componentData.id,
        setProduct
      );

      if (productDetails && productDetails.success) {
        // Fetch product logs
        const logsResponse = await fetchProductLogs(token, componentData.id);
        if (logsResponse && logsResponse.success) {
          setProductLogs(logsResponse.data);
        }
      }
    } catch (error) {
      // Error handled silently
    } finally {
      setIsLoading(false);
    }
  };

  // Function to filter out a log by its ID
  const deleteProductLogFromState = logIdToDelete => {
    const filteredLogs = productLogs.filter(log => log.id !== logIdToDelete);
    setProductLogs(filteredLogs);
  };

  // Function to refresh product data
  const fetchProduct = async () => {
    if (componentData.id) {
      setIsLoading(true);
      try {
        await getProductDetailsOptimized(token, componentData.id, setProduct);
      } catch (error) {
        // Error handled silently
      } finally {
        setIsLoading(false);
      }
    }
  };

  /**
   * Refresh kit product details after a content-lot open without replacing
   * the modal body with the loading spinner.
   */
  const refreshProductQuietly = async () => {
    if (!componentData.id) return;
    try {
      await getProductDetailsOptimized(token, componentData.id, setProduct);
    } catch (error) {
      // Error handled silently
    }
  };

  /**
   * Re-read this kit after a related-part stock change (component-sync).
   *
   * @param {{ componentId?: * }} [meta]
   */
  const handleKitComponentStockSync = async (meta = {}) => {
    if (!componentData.id || !token) return;
    logInfo('ComponentInventoryModal', 'kit component-sync start', {
      productId: componentData.id,
      componentId: meta.componentId ?? null,
    });
    try {
      const details = await getProductDetailsDirect(token, componentData.id);
      if (details && typeof details === 'object') {
        setProduct(prev => ({ ...(prev || {}), ...details }));
      } else {
        logError('ComponentInventoryModal', 'kit component-sync missing details', {
          productId: componentData.id,
        });
        await refreshProductQuietly();
      }
    } catch (error) {
      logError('ComponentInventoryModal', 'kit component-sync failed', {
        productId: componentData.id,
      });
      await refreshProductQuietly();
    }
  };

  const kitStockDisplay =
    product && isKitProduct(product) ? getKitStockDisplay(product) : null;
  const openedLotCount = product
    ? resolveOpenedLotCount(
        Array.isArray(product.items)
          ? {
              items: product.items,
              opened_lot_count: product.opened_lot_count,
            }
          : { opened_lot_count: product.opened_lot_count },
      )
    : null;
  const lotLocationGroups = groupLotLocations(product?.items, storageTree, product?.location);
  const locationText = String(product?.location || '').trim();
  const componentLots = Array.isArray(product?.items) ? product.items : [];
  const lotsAllPlaced = allStockItemsHaveStorageNode(componentLots);

  useEffect(() => {
    if (!product?.id || !locationText || lotsAllPlaced) return;
    logInfo('ComponentInventoryModal', 'product location string shown', {
      productId: product.id,
      locationLength: locationText.length,
      lotCount: componentLots.length,
    });
  }, [product?.id, locationText, lotsAllPlaced, componentLots.length]);

  useEffect(() => {
    if (!product?.id || !lotsAllPlaced) return;
    logInfo('ComponentInventoryModal', 'product location string hidden', {
      productId: product.id,
      lotCount: componentLots.length,
      locationLength: locationText.length,
    });
  }, [product?.id, lotsAllPlaced, componentLots.length, locationText.length]);

  /**
   * Keep this modal's lots in sync after create, In Use toggle, or delete
   * so the opened-lot count updates without another get_details.
   *
   * @param {{ data?: Object|Object[], deleteItem?: boolean, replaceSingleItem?: boolean, replaceItems?: boolean }} payload
   */
  const updateStockItems = ({
    data,
    deleteItem = false,
    replaceSingleItem = false,
    replaceItems = false,
  }) => {
    setProduct(prev => {
      if (!prev) return prev;
      const current = Array.isArray(prev.items) ? prev.items : [];
      let next = current;
      if (deleteItem) {
        next = current.filter(item => item.id !== data?.id);
      } else if (replaceSingleItem && data) {
        next = current.map(item => (item.id === data.id ? data : item));
      } else if (Array.isArray(data)) {
        next = replaceItems ? data : [...current, ...data];
      } else if (data) {
        next = [...current, data];
      }
      logInfo('ComponentInventoryModal', 'stock items updated', {
        productId: prev.id,
        itemCount: next.length,
        openedLotCount: next.filter(item => item?.in_use).length,
      });
      return { ...prev, items: next };
    });
  };

  return (
    <>
      {/* Button to open the modal, displaying the component's name */}
      <Button
        variant="link"
        onClick={handleShow}
        style={{
          padding: 0,
          textDecoration: 'none',
          color: '#0d6efd',
        }}
        className="p-0"
      >
        <strong>{componentData.name}</strong>
      </Button>

      {/* The Modal component that appears upon clicking the button */}
      <Modal
        show={show}
        onHide={handleClose}
        aria-labelledby="component-product-modal"
        fullscreen={true}
        enforceFocus={false}
      >
        {/* Modal header with the component product name as the title */}
        <Modal.Header closeButton>
          <Modal.Title>
            {product ? product.name : componentData.name}
            <small className="text-muted ms-2">(Component Product)</small>
          </Modal.Title>
        </Modal.Header>

        {/* Modal body containing product details */}
        <Modal.Body>
          {/* Show loading spinner if data is still being fetched */}
          {isLoading || !product ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: '400px' }}
            >
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <Container>
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">Catalogue Number: </p>
                </Col>
                <Col>
                  <p className="fs-6">{product.cat_num}</p>
                </Col>
              </Row>
              <div className="inventory-modal-details-split">
                <div className="inventory-modal-fields-column">
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">
                    {isKitProduct(product)
                      ? 'Complete kits on hand:'
                      : 'Stock:'}
                  </p>
                </Col>
                <Col>
                  {isKitProduct(product) ? (
                    <p className="fs-6">
                      {kitStockDisplay.stock === null
                        ? null
                        : kitStockDisplay.stock}
                      {kitStockDisplay.stock !== null && (
                        <span className="text-muted ms-2">
                          (from components)
                        </span>
                      )}
                      {kitStockDisplay.hasOpenKitLeftover && (
                        <span className="d-block text-muted small mt-1">
                          Leftover units are open kit lots that could not be
                          removed (completable from components:{' '}
                          {kitStockDisplay.effectiveStock}). Kit count is not
                          reduced to consume components.
                        </span>
                      )}
                    </p>
                  ) : (
                    <p className="fs-6 mb-0">
                      {getEffectiveStock(product) === null
                        ? null
                        : `${getEffectiveStock(product)} ${
                            product.unit === 'Package' || product.unit === 'Box'
                              ? `(${
                                  product.items?.reduce(
                                    (total, item) =>
                                      total + (item?.item_sub_stock || 0),
                                    0
                                  ) || 0
                                })`
                              : ''
                          }`}
                    </p>
                  )}
                  <OpenedLotCount count={openedLotCount} />
                </Col>
              </Row>
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">Category: </p>
                </Col>
                <Col>
                  <p className="fs-6">{product.category}</p>
                </Col>
              </Row>
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">Supplier: </p>
                </Col>
                <Col>
                  <SupplierDetailModal
                    supplierObj={product.supplier}
                    supplierId={product.supplier?.id}
                    isSupplier={isSupplier}
                  />
                </Col>
              </Row>
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">Manufacturer: </p>
                </Col>
                <Col>
                  {product?.manufacturer && (
                    <ManufacturerDetailModal
                      isSupplier={isSupplier}
                      manufacturerObj={product.manufacturer}
                      manufacturerId={product.manufacturer.id}
                    />
                  )}
                </Col>
              </Row>
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">Unit: </p>
                </Col>
                <Col>
                  <p className="fs-6">
                    {product.unit === 'Assays'
                      ? 'Reactions / Tests / Assays'
                      : product.unit}
                  </p>
                </Col>
              </Row>
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">Unit Quantity: </p>
                </Col>
                <Col>
                  <p className="fs-6">
                    {product.unit_quantity !== null &&
                    product.unit_quantity !== undefined
                      ? formatDecimalNumber(product.unit_quantity)
                      : ''}
                  </p>
                </Col>
              </Row>
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">Units Per Sub Unit: </p>
                </Col>
                <Col>
                  <p className="fs-6">{product.units_per_sub_unit}</p>
                </Col>
              </Row>
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">Storage: </p>
                </Col>
                <Col>
                  <p className="fs-6">{product.storage}</p>
                </Col>
              </Row>
              {!lotsAllPlaced && (
                <Row md={3}>
                  <Col>
                    <p className="fs-6 fw-bold">Location: </p>
                  </Col>
                  <Col>
                    <p className="fs-6">{locationText || '-'}</p>
                  </Col>
                </Row>
              )}
              <Row md={3}>
                <Col>
                  <p className="fs-6 fw-bold">Price: </p>
                </Col>
                <Col>
                  <div className="fs-6">
                    <div>
                      <span>
                        Pre-Discount:{' '}
                        {product.price !== null && product.price !== undefined
                          ? `${formatDecimalNumber(product.price)}${
                              CURRENCY_SYMBOLS[product.currency]
                                ? `${getCurrencySymbol(product.currency)}`
                                : ''
                            }`
                          : ''}
                      </span>
                    </div>
                    {product.discount && (
                      <div>
                        <span>
                          Post-Discount:{' '}
                          {product.price !== null &&
                          product.price !== undefined &&
                          product.discount !== null &&
                          product.discount !== undefined
                            ? `${calculatePriceAfterDiscount(
                                product.price,
                                product.discount
                              )}${
                                CURRENCY_SYMBOLS[product.currency]
                                  ? ` ${getCurrencySymbol(product.currency)}`
                                  : ''
                              } (-${
                                product.discount !== null &&
                                product.discount !== undefined
                                  ? formatDecimalNumber(product.discount)
                                  : ''
                              }%)`
                            : ''}
                        </span>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
              <Row md={3} className={'mt-2'}>
                <Col className="mt-1">
                  <p className="fs-6 fw-bold">Product Profile: </p>
                </Col>
                <Col>
                  {product.url && (
                    <a
                      href={product.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="d-flex align-items-center"
                      style={{ padding: 0 }}
                    >
                      <span className="me-2">Manufacturer Website</span>
                      <NavigateToUrlIcon iconSize={'1.2rem'} />
                    </a>
                  )}
                </Col>
              </Row>
              <Row md={3}>
                <Col className="mt-1">
                  <p className="fs-6 fw-bold">Expiry Relevant: </p>
                </Col>
                {product.expiry_relevant !== '0' &&
                  product.expiry_relevant !== 'false' &&
                  product.expiry_relevant && (
                    <Col>{getExpiryCategoryLabel(product.expiry_relevant)}</Col>
                  )}
              </Row>
              <Row md={3}>
                <Col className="mt-1">
                  <p className="fs-6 fw-bold">Notes: </p>
                </Col>
                <Col md={8} className="bordered-shadow-container">
                  {product.notes !== null && product.notes}
                </Col>
              </Row>
              {!isSupplier && (
                <div className="inventory-modal-locations">
                  <ProductLotLocations groups={lotLocationGroups} />
                </div>
              )}
                </div>
                <div className="inventory-modal-actions-column">
                  {!isKitProduct(product) && (
                    <UpdateAmountModal
                      product={{
                        catNum: product.cat_num,
                        productId: product.id,
                        currentStock: product.stock,
                        ...product,
                      }}
                      onSuccessfulUpdate={() => fetchProduct()}
                    />
                  )}
                  <div className="inventory-modal-procurement-under-stock">
                    <ProductOpeningStatisticsSection product={product} />
                  </div>
                </div>
              </div>

              {isKitProduct(product) && (
                <KitInventoryPanel
                  product={product}
                  token={token}
                  isSupplier={isSupplier}
                  onOpeningHistoryChange={refreshProductQuietly}
                  onComponentStockSync={handleKitComponentStockSync}
                  storageNodes={storageTree}
                />
              )}

              {/* Multi-Component Status - Only show for products with multi-component support */}
              {product.supports_multi_components && (
                <>
                  <hr />
                  <Row md={3}>
                    <Col>
                      <p className="fs-6 fw-bold">Multi-Component Status: </p>
                    </Col>
                    <Col>
                      <div className="d-flex align-items-center">
                        <span className="badge bg-success me-2">Enabled</span>
                        <span className="text-muted">
                          {product.multi_component_status?.total_components ||
                            0}{' '}
                          components defined
                        </span>
                      </div>
                    </Col>
                  </Row>

                  {/* Multi-Component Details */}
                  {product.multi_component_status?.components &&
                    product.multi_component_status.components.length > 0 && (
                      <Row className="mt-3">
                        <Col>
                          <div className="border rounded p-3 bg-light">
                            <h6 className="mb-3">Component Details:</h6>
                            <div className="row">
                              {product.multi_component_status.components.map(
                                (component, index) => (
                                  <div key={index} className="col-12 mb-2">
                                    <div className="d-flex justify-content-between align-items-center p-2 border rounded bg-white">
                                      <div>
                                        <strong>
                                          {component.name} ({component.cat_num})
                                        </strong>
                                        <br />
                                        <small className="text-muted">
                                          Quantity:{' '}
                                          {component.quantity_per_component} |
                                          Location: {component.location} |
                                          Stock: {component.component_stock} |
                                          Required:{' '}
                                          {component.is_required ? 'Yes' : 'No'}
                                        </small>
                                      </div>
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    )}
                  <hr />
                </>
              )}

              {/* Bootstrap Carousel component to display product images */}
              <hr />
              <CarouselComponent
                images={product.images}
                imageIdKind="productImageId"
              />
              <hr />

              {/* Bootstrap Table component to display the related items to the product currently in stock */}
              {!isSupplier && (
                <>
                  <StockItemTable
                    product={product}
                    items={product.items || []}
                    addNewItem={false}
                    addStockItem={isViewer ? undefined : () => {}}
                    updateStockItems={updateStockItems}
                    updateProductStock={() => {}}
                    showBar={false}
                    setTotalItemStock={() => {}}
                    updateProductLogs={() => {}}
                    onViewMultiComponents={() => {}}
                    headerLabel={
                      isKitProduct(product)
                        ? 'Kit lots (open as a unit)'
                        : 'Stock Items'
                    }
                    storageNodes={storageTree}
                  />
                </>
              )}

              {!isSupplier &&
                product?.competing_products &&
                product.competing_products.length > 0 && (
                  <>
                    <SimilarProductsTable
                      similarProducts={product.competing_products}
                      baseProduct={product}
                    />
                  </>
                )}

              {productLogs.length > 0 && (
                <>
                  <StockItemLogsTable
                    stockItemLogs={productLogs}
                    onSuccessfulDelete={deleteProductLogFromState}
                  />
                </>
              )}
            </Container>
          )}
        </Modal.Body>

        {/* Modal footer with action buttons */}
        <Modal.Footer className="d-flex flex-row justify-content-between">
          {!isSupplier && !isViewer && product && (
            <div className="d-flex flex-row">
              <div className="me-2">
                <ProductModal
                  productObj={product}
                  clearSearchValue={clearSearchValue}
                  onSuccessfulSubmit={() => {
                    if (isKitProduct(product)) {
                      handleKitComponentStockSync();
                    } else {
                      fetchProduct();
                    }
                  }}
                  handleProductUpdateFromSupplierTable={
                    handleProductUpdateFromSupplierTable
                  }
                />
              </div>
              <DeleteButton
                objectType="product"
                objectName={product.name}
                objectId={product.id}
                deleteFetchFunc={deleteProduct}
                onSuccessfulDelete={updateProducts}
                clearSearchValue={clearSearchValue}
              />
            </div>
          )}
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ComponentInventoryModal;
